import { create } from 'zustand';
import { supabase } from '../supabase';
import { friendlyDbError } from '../utils/friendlyDbError';
import {
  defaultShown,
  visibleCalendars,
  type CalendarPerson,
  type CalendarShare,
  type ColorMode,
  type ShareLevel,
  type ShareOwnerType,
  type ShareSubjectType,
} from '../calendar/sharing';

/**
 * Calendari personali condivisi: persone, reparti, regole (`calendar_shares`), calendari che vedo e quali ho acceso.
 * Le scelte personali (accesi/spenti, colori) stanno in `user_profiles.calendar_preferences`.
 */
export interface Department { id: string; name: string; color: string; manager_user_id: string | null; position: number }

export interface CalendarPreferences {
  color_mode?: ColorMode;
  person_colors?: Record<string, string>;
  /** calendari spenti; assente = valori di partenza (il mio e quelli in dettaglio) */
  hidden_calendars?: string[];
  external_visibility?: Record<string, 'none' | 'busy' | 'title' | 'details'>;
  /** calendari esterni che mi rendono occupata ("google:<id>" / "microsoft:<id>") */
  busy_calendars?: string[];
  /** vista Team: reparto e persone scelte l'ultima volta */
  team?: { department_id?: string; people?: string[] | null };
}

interface Context { meId: string; organizationId: string; canSeeAll: boolean; canEditOthers: boolean }

export type ShareRule = { owner_type: ShareOwnerType; owner_id: string; subject_type: ShareSubjectType; subject_id: string | null; level: ShareLevel };

interface CalendarSharingState {
  ready: boolean;
  loading: boolean;
  error: string | null;
  ctx: Context | null;
  people: CalendarPerson[];
  roles: { id: string; name: string }[];
  departments: Department[];
  shares: CalendarShare[];
  /** id persona -> livello, solo per i calendari che vedo (il mio incluso) */
  visible: Record<string, ShareLevel>;
  /** calendari accesi */
  shown: Record<string, true>;
  prefs: CalendarPreferences;
  load: (ctx: Context) => Promise<void>;
  togglePerson: (id: string) => void;
  showOnly: (ids: string[]) => void;
  showAll: (ids: string[], on: boolean) => void;
  setPrefs: (patch: Partial<CalendarPreferences>) => Promise<void>;
  saveShare: (rule: ShareRule) => Promise<boolean>;
  deleteShare: (id: string) => Promise<boolean>;
  refreshShares: () => Promise<void>;
}

function recompute(ctx: Context, people: CalendarPerson[], shares: CalendarShare[]): Record<string, ShareLevel> {
  return Object.fromEntries(visibleCalendars({ meId: ctx.meId, people, shares, canSeeAll: ctx.canSeeAll, canEditOthers: ctx.canEditOthers }));
}

/**
 * Calendari accesi. Alla prima apertura partono solo quelli condivisi con una regola (reparto, ruolo, persona,
 * "partenza come prima"): chi vede tutti per permesso (amministratori) li trova nell'elenco ma spenti,
 * cosi' dopo il rilascio il calendario di chi c'era gia' resta com'era.
 */
function computeShown(visible: Record<string, ShareLevel>, shared: Record<string, ShareLevel>, meId: string, prefs: CalendarPreferences, bookedFor?: ReadonlySet<string>): Record<string, true> {
  const ids = Object.keys(visible);
  const set = prefs.hidden_calendars
    ? new Set(ids.filter(id => !prefs.hidden_calendars!.includes(id)))
    : defaultShown(new Map(Object.entries(shared)), meId, bookedFor);
  return Object.fromEntries([...set].filter(id => id in visible).map(id => [id, true as const]));
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export const useCalendarSharingStore = create<CalendarSharingState>((set, get) => ({
  ready: false,
  loading: false,
  error: null,
  ctx: null,
  people: [],
  roles: [],
  departments: [],
  shares: [],
  visible: {},
  shown: {},
  prefs: {},

  load: async (ctx) => {
    set({ loading: true, error: null, ctx });
    try {
      const [peopleRes, rolesRes, depsRes, udRes, sharesRes, prefsRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('id, full_name, email, user_roles(role_id)')
          .eq('organization_id', ctx.organizationId)
          .eq('active', true)
          .order('full_name', { ascending: true }),
        supabase.from('roles').select('id, name').eq('organization_id', ctx.organizationId).order('name'),
        supabase.from('departments').select('id, name, color, manager_user_id, position').eq('organization_id', ctx.organizationId).order('position').order('name'),
        supabase.from('user_departments').select('user_id, department_id').eq('organization_id', ctx.organizationId),
        supabase.from('calendar_shares').select('*').eq('organization_id', ctx.organizationId),
        supabase.from('user_profiles').select('calendar_preferences').eq('id', ctx.meId).maybeSingle(),
      ]);
      if (peopleRes.error) throw peopleRes.error;
      if (rolesRes.error) throw rolesRes.error;
      // Tabelle nuove non ancora presenti (migration non applicata): si lavora come prima, senza reparti ne' condivisioni
      for (const r of [depsRes, udRes, sharesRes, prefsRes]) if (r.error) console.warn('[calendarSharing] non disponibile:', r.error.message);
      const deptByUser = new Map<string, string[]>();
      for (const row of (udRes.error ? [] : udRes.data || []) as { user_id: string; department_id: string }[]) {
        deptByUser.set(row.user_id, [...(deptByUser.get(row.user_id) || []), row.department_id]);
      }
      const people: CalendarPerson[] = (peopleRes.data || []).map((p: any) => ({
        id: p.id,
        name: p.full_name || p.email || 'Senza nome',
        roleIds: (p.user_roles || []).map((r: { role_id: string }) => r.role_id),
        departmentIds: deptByUser.get(p.id) || [],
      }));
      const shares = sharesRes.error ? [] : ((sharesRes.data || []) as CalendarShare[]);
      const prefs = ((prefsRes.error ? null : (prefsRes.data as any)?.calendar_preferences) || {}) as CalendarPreferences;
      const visible = recompute(ctx, people, shares);
      const shared = recompute({ ...ctx, canSeeAll: false, canEditOthers: false }, people, shares);
      // Prima apertura con tanti calendari condivisi: si parte da quelli delle persone per cui ho fissato appuntamenti (90 giorni)
      let bookedFor: Set<string> | undefined;
      if (!prefs.hidden_calendars && Object.keys(shared).length > 16) {
        const since = new Date(Date.now() - 90 * 86400000).toISOString();
        const { data } = await supabase.from('sales_activities').select('assigned_to')
          .eq('organization_id', ctx.organizationId).eq('user_id', ctx.meId).gte('created_at', since)
          .not('assigned_to', 'is', null).neq('assigned_to', ctx.meId).limit(1000);
        bookedFor = new Set(((data || []) as { assigned_to: string }[]).map(r => r.assigned_to));
      }
      set({
        people,
        roles: rolesRes.data || [],
        departments: (depsRes.error ? [] : depsRes.data || []) as Department[],
        shares,
        prefs,
        visible,
        shown: computeShown(visible, shared, ctx.meId, prefs, bookedFor),
        ready: true,
        loading: false,
      });
    } catch (error) {
      // Senza persone si vede solo il proprio calendario, come prima
      set({ error: friendlyDbError(error), loading: false, ready: true, visible: { [ctx.meId]: 'edit' }, shown: { [ctx.meId]: true } });
    }
  },

  togglePerson: (id) => {
    const shown = { ...get().shown };
    if (shown[id]) delete shown[id]; else shown[id] = true;
    set({ shown });
    void get().setPrefs({ hidden_calendars: Object.keys(get().visible).filter(v => !shown[v]) });
  },

  showOnly: (ids) => {
    const keep = new Set(ids);
    const shown = Object.fromEntries(Object.keys(get().visible).filter(id => keep.has(id)).map(id => [id, true as const]));
    set({ shown });
    void get().setPrefs({ hidden_calendars: Object.keys(get().visible).filter(v => !shown[v]) });
  },

  showAll: (ids, on) => {
    const shown = { ...get().shown };
    for (const id of ids) { if (on) shown[id] = true; else delete shown[id]; }
    set({ shown });
    void get().setPrefs({ hidden_calendars: Object.keys(get().visible).filter(v => !shown[v]) });
  },

  setPrefs: async (patch) => {
    const { ctx } = get();
    const prefs = { ...get().prefs, ...patch };
    set({ prefs });
    if (!ctx) return;
    // Un salvataggio ogni tanto: le spunte si cliccano a raffica
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      const { error } = await supabase.from('user_profiles').update({ calendar_preferences: get().prefs }).eq('id', ctx.meId);
      if (error) console.warn('[calendarSharing] preferenze non salvate:', error.message);
    }, 600);
  },

  saveShare: async (rule) => {
    const { ctx, people } = get();
    if (!ctx) return false;
    const existing = get().shares.find(s => s.owner_type === rule.owner_type && s.owner_id === rule.owner_id
      && s.subject_type === rule.subject_type && (s.subject_id ?? null) === (rule.subject_id ?? null));
    const q = existing
      ? supabase.from('calendar_shares').update({ level: rule.level }).eq('id', existing.id).select('*').single()
      : supabase.from('calendar_shares').insert({ ...rule, organization_id: ctx.organizationId }).select('*').single();
    const { data, error } = await q;
    if (error) { set({ error: friendlyDbError(error) }); return false; }
    const shares = existing ? get().shares.map(s => s.id === existing.id ? (data as CalendarShare) : s) : [...get().shares, data as CalendarShare];
    const visible = recompute(ctx, people, shares);
    set({ shares, visible, error: null });
    return true;
  },

  deleteShare: async (id) => {
    const { ctx, people } = get();
    if (!ctx) return false;
    const { error } = await supabase.from('calendar_shares').delete().eq('id', id);
    if (error) { set({ error: friendlyDbError(error) }); return false; }
    const shares = get().shares.filter(s => s.id !== id);
    set({ shares, visible: recompute(ctx, people, shares), error: null });
    return true;
  },

  refreshShares: async () => {
    const { ctx } = get();
    if (ctx) await get().load(ctx);
  },
}));

export const calendarSharingStore = useCalendarSharingStore;
